<!-- Page Info -->
<div class="alert alert-light-primary d-flex align-items-start gap-3 mb-6 border border-primary border-opacity-25 rounded-3 p-4">
  <i class="fa fa-circle-info fs-2 text-primary mt-1"></i>
  <div>
    <h5 class="fw-bold mb-2">{{ 'manageEvent.trainingPackages.infoTitle' | translate }}</h5>
    <p class="text-gray-700 mb-2">{{ 'manageEvent.trainingPackages.infoDescription' | translate }}</p>
    <ul class="text-gray-600 fs-7 mb-0 ps-4">
      <li>{{ 'manageEvent.trainingPackages.infoStep1' | translate }}</li>
      <li>{{ 'manageEvent.trainingPackages.infoStep2' | translate }}</li>
      <li>{{ 'manageEvent.trainingPackages.infoStep3' | translate }}</li>
    </ul>
  </div>
</div>

<!-- Summary Cards -->
<div class="pb-5">
<div class="row g-5 mb-8">
  <div class="col-md-4">
    <div class="card bg-light-primary">
      <div class="card-body p-5">
        <div class="d-flex align-items-center">
          <i class="fa fa-box-open fs-2x text-primary me-4"></i>
          <div>
            <div class="fs-7 text-muted">{{ 'manageEvent.trainingPackages.totalPackages' | translate }}</div>
            <div class="fs-2 fw-bold text-gray-800">{{ packages.length }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card bg-light-success">
      <div class="card-body p-5">
        <div class="d-flex align-items-center">
          <i class="fa fa-users fs-2x text-success me-4"></i>
          <div>
            <div class="fs-7 text-muted">{{ 'manageEvent.trainingPackages.totalRegistrations' | translate }}</div>
            <div class="fs-2 fw-bold text-gray-800">{{ totalRegistrations }} ({{ totalPaidRegistrations }} {{ 'manageEvent.trainingPackages.paid' | translate }})</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card bg-light-warning">
      <div class="card-body p-5">
        <div class="d-flex align-items-center">
          <i class="fa fa-money-bill fs-2x text-warning me-4"></i>
          <div>
            <div class="fs-7 text-muted">{{ 'manageEvent.trainingPackages.totalRevenue' | translate }}</div>
            <div class="fs-2 fw-bold text-gray-800">{{ totalRevenue | number:'1.2-2' }} {{ currency }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Packages Table -->
<div class="card card-flush">
  <div class="card-header mt-5">
    <div class="card-title flex-column">
      <h3 class="fw-bold mb-1">{{ 'manageEvent.trainingPackages.title' | translate }}</h3>
      <div class="fs-6 text-gray-500">
        {{ 'manageEvent.trainingPackages.subtitle' | translate }}
      </div>
    </div>
    <div class="card-toolbar">
      <button class="btn btn-sm btn-primary" (click)="openAddPackageModal()">
        <i class="fa fa-plus me-2"></i>{{ 'manageEvent.trainingPackages.addPackage' | translate }}
      </button>
    </div>
  </div>
  <div class="card-body pt-0">
    @if (loading) {
      <div class="text-center py-10">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
        </div>
      </div>
    } @else if (packages.length === 0) {
      <div class="text-center py-10 text-muted">
        <i class="fa fa-box-open fs-3x mb-4 d-block opacity-50"></i>
        <p class="mb-0">{{ 'manageEvent.trainingPackages.noPackages' | translate }}</p>
        <p class="fs-7">{{ 'manageEvent.trainingPackages.noPackagesHint' | translate }}</p>
      </div>
    } @else {
      <div class="table-responsive">
        <table class="table table-row-bordered table-row-dashed gy-4 align-middle fw-bold">
          <thead class="fs-7 text-gray-500 text-uppercase">
            <tr>
              <th class="min-w-200px">{{ 'manageEvent.trainingPackages.tablePackage' | translate }}</th>
              <th class="min-w-100px">{{ 'manageEvent.trainingPackages.tableSessions' | translate }}</th>
              <th class="min-w-100px">{{ 'manageEvent.trainingPackages.tableType' | translate }}</th>
              <th class="min-w-100px">{{ 'manageEvent.trainingPackages.tablePrice' | translate }}</th>
              <th class="min-w-150px">{{ 'manageEvent.trainingPackages.tableRegistrations' | translate }}</th>
              <th class="min-w-100px">{{ 'manageEvent.trainingPackages.tableRevenue' | translate }}</th>
              <th class="text-end min-w-70px">{{ 'manageEvent.trainingPackages.tableActions' | translate }}</th>
            </tr>
          </thead>
          <tbody class="fs-6">
            @for (pkg of packages; track pkg.package_id) {
              <tr class="cursor-pointer" (click)="openViewRegistrationsModal(pkg)">
                <td>
                  <div class="d-flex flex-column">
                    <span class="fw-bold text-gray-800">{{ pkg.package_name }}</span>
                    @if (pkg.package_description) {
                      <span class="text-muted fs-7 text-truncate" style="max-width: 250px;">{{ pkg.package_description }}</span>
                    }
                  </div>
                </td>
                <td>
                  <span class="badge badge-light-primary">{{ pkg.session_count }} {{ pkg.session_count !== 1 ? ('manageEvent.trainingPackages.sessions' | translate) : ('manageEvent.trainingPackages.session' | translate) }}</span>
                </td>
                <td>
                  <span class="badge badge-light-info">{{ getSessionTypeLabel(pkg.session_type) }}</span>
                </td>
                <td>
                  <span class="fw-semibold">{{ formatPrice(pkg.price_cents, pkg.currency) }}</span>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="fw-bold">{{ getPackageRegistrationCount(pkg.package_id) }}</span>
                    @if (pkg.max_registrations) {
                      <span class="text-muted">/ {{ pkg.max_registrations }}</span>
                    }
                    @if (getPaidRegistrationCount(pkg.package_id) > 0) {
                      <span class="badge badge-light-success">{{ getPaidRegistrationCount(pkg.package_id) }} {{ 'manageEvent.trainingPackages.paid' | translate }}</span>
                    }
                    @if (getPendingRegistrationCount(pkg.package_id) > 0) {
                      <span class="badge badge-light-warning">{{ getPendingRegistrationCount(pkg.package_id) }} {{ 'manageEvent.trainingPackages.pending' | translate }}</span>
                    }
                  </div>
                </td>
                <td>
                  <span class="fw-semibold text-success">{{ getPackageNetRevenue(pkg) | number:'1.2-2' }} {{ pkg.currency }}</span>
                </td>
                <td class="text-end" (click)="$event.stopPropagation()">
                  <div class="dropdown">
                    <button
                      class="btn btn-sm btn-icon btn-light btn-active-light-primary"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i class="fa fa-ellipsis-v"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li>
                        <a class="dropdown-item" (click)="openViewRegistrationsModal(pkg)">
                          <i class="far fa-eye me-2"></i>
                          {{ 'manageEvent.trainingPackages.viewRegistrations' | translate }}
                        </a>
                      </li>
                      <li>
                        <a class="dropdown-item" (click)="openEditPackageModal(pkg)">
                          <i class="far fa-edit me-2"></i>
                          {{ 'manageEvent.trainingPackages.editPackage' | translate }}
                        </a>
                      </li>
                      <li>
                        <a class="dropdown-item" (click)="openMembershipPricingModal(pkg)">
                          <i class="fas fa-tag me-2"></i>
                          {{ 'manageEvent.trainingPackages.membershipPricing' | translate }}
                        </a>
                      </li>
                      <li><hr class="dropdown-divider"></li>
                      <li>
                        <a class="dropdown-item text-danger" (click)="deletePackage(pkg)">
                          <i class="far fa-trash-alt me-2"></i>
                          {{ 'manageEvent.trainingPackages.deletePackage' | translate }}
                        </a>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>
</div>
